import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle, XCircle, Video, ChevronDown, ChevronUp, RotateCcw, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const chapters = [
    { id: "ch-1", title: "Reguli generale" },
    { id: "ch-2", title: "Indicatoare și marcaje" },
    { id: "ch-3", title: "Semnalele luminoase și sonore" },
    { id: "ch-4", title: "Semnalele polițiștilor" },
    { id: "ch-5", title: "Poziția în timpul mersului" },
    { id: "ch-6", title: "Depășirea" },
    { id: "ch-7", title: "Viteza și distanța între vehicule" },
    { id: "ch-8", title: "Reguli referitoare la manevre" },
    { id: "ch-9", title: "Intersecții și prioritate de trecere" },
    { id: "ch-10", title: "Trecerea la nivel cu calea ferată" },
    { id: "ch-11", title: "Oprirea, staționarea și parcarea" },
    { id: "ch-12", title: "Circulația pe autostradă" },
    { id: "ch-13", title: "Obligațiile conducătorilor de autovehicule" },
    { id: "ch-14", title: "Sancțiuni și contravenții" },
    { id: "ch-15", title: "Conducerea preventivă" },
    { id: "ch-16", title: "Măsuri de prim ajutor" },
    { id: "ch-17", title: "Conducerea ecologică" },
    { id: "ch-18", title: "Noțiuni de mecanică" },
];

const LETTER_TO_INDEX = { a: 0, b: 1, c: 2, d: 3, e: 4, f: 5 };
const INDEX_TO_LETTER = ["A", "B", "C", "D", "E", "F"];

function normalizeToIndices(value) {
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "number") return item;
                const key = String(item).trim().toLowerCase();
                return LETTER_TO_INDEX[key];
            })
            .filter((v) => Number.isInteger(v))
            .sort((a, b) => a - b);
    }

    if (typeof value === "number") return [value];

    if (value !== undefined && value !== null && value !== "") {
        const key = String(value).trim().toLowerCase();
        const mapped = LETTER_TO_INDEX[key];
        if (Number.isInteger(mapped)) return [mapped];
    }

    return [];
}

function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
}

// Componenta pentru afișarea unei întrebări greșite
function WrongAnswerCard({ item, index }) {
    const [expanded, setExpanded] = useState(false);
    
    // Convertim răspunsul utilizatorului în litere
    const getUserAnswerLetters = () => {
        if (item.user_answer === undefined || item.user_answer === null) return "Niciun răspuns";
        if (Array.isArray(item.user_answer)) {
            return item.user_answer.map(i => INDEX_TO_LETTER[i] || i).join(", ");
        }
        return INDEX_TO_LETTER[item.user_answer] || item.user_answer;
    };
    
    // Convertim răspunsul corect în litere
    const getCorrectAnswerLetters = () => {
        if (Array.isArray(item.correct_answer)) {
            return item.correct_answer.map(c => String(c).toUpperCase()).join(", ");
        }
        return String(item.correct_answer || "").toUpperCase();
    };
    
    // Găsim textul opțiunii bazat pe literă
    const getOptionText = (letter) => {
        const letterLower = String(letter).toLowerCase();
        const idx = LETTER_TO_INDEX[letterLower];
        if (idx !== undefined && item.options && item.options[idx]) {
            return item.options[idx];
        }
        return "";
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
            {/* Header întrebare */}
            <div 
                className="p-4 cursor-pointer hover:bg-red-50 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-slate-800 font-medium leading-snug">
                            {item.question}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm">
                            <span className="text-red-600">
                                Răspunsul tău: <strong>{getUserAnswerLetters()}</strong>
                            </span>
                            <span className="text-green-600">
                                Corect: <strong>{getCorrectAnswerLetters()}</strong>
                            </span>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        {expanded ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                    </div>
                </div>
            </div>

            {/* Detalii expandate */}
            {expanded && (
                <div className="border-t border-red-100 p-4 bg-slate-50">
                    {/* Imagine dacă există */}
                    {item.image_url && (
                        <div className="mb-4">
                            <img 
                                src={item.image_url} 
                                alt="Imagine întrebare" 
                                className="max-w-full max-h-48 rounded-lg border mx-auto"
                                onError={(e) => e.target.style.display = 'none'}
                            />
                        </div>
                    )}

                    {/* Opțiunile cu evidențiere */}
                    <div className="space-y-2 mb-4">
                        {item.options?.map((opt, optIdx) => {
                            const letter = INDEX_TO_LETTER[optIdx]?.toLowerCase();
                            const isCorrect = Array.isArray(item.correct_answer) 
                                ? item.correct_answer.includes(letter)
                                : String(item.correct_answer).toLowerCase() === letter;
                            const isUserChoice = Array.isArray(item.user_answer)
                                ? item.user_answer.includes(optIdx)
                                : item.user_answer === optIdx;
                            
                            return (
                                <div 
                                    key={optIdx}
                                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                                        isCorrect 
                                            ? 'bg-green-50 border-green-300' 
                                            : isUserChoice 
                                                ? 'bg-red-50 border-red-300' 
                                                : 'bg-white border-slate-200'
                                    }`}
                                >
                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                        isCorrect 
                                            ? 'bg-green-500 text-white' 
                                            : isUserChoice 
                                                ? 'bg-red-500 text-white' 
                                                : 'bg-slate-200 text-slate-600'
                                    }`}>
                                        {INDEX_TO_LETTER[optIdx]}
                                    </span>
                                    <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' : isUserChoice ? 'text-red-800' : 'text-slate-700'}`}>
                                        {opt}
                                    </span>
                                    {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                                    {isUserChoice && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                                </div>
                            );
                        })}
                    </div>

                    {/* Explicație text */}
                    {item.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                            <p className="text-sm font-medium text-blue-800 mb-1">Explicație:</p>
                            <p className="text-blue-700">{item.explanation}</p>
                        </div>
                    )}

                    {/* Buton video explicativ */}
                    {item.explanation_video_url && (
                        <a 
                            href={item.explanation_video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            <Video className="w-4 h-4" />
                            Vezi explicația video
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ChapterQuizPage() {
    const { chapterId } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [currentIndex, setCurrentIndex] = useState(0);

    // răspunsuri CONFIRMATE prin Următoarea / Răspunde
    const [answers, setAnswers] = useState({});

    // răspunsul selectat pe întrebarea curentă, dar încă neconfirmat
    const [currentAnswer, setCurrentAnswer] = useState(undefined);

    const [result, setResult] = useState(null);

    useEffect(() => {
        fetchQuiz();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chapterId]);

    useEffect(() => {
        setImageError(false);
    }, [currentIndex]);

    useEffect(() => {
        if (!quiz?.questions?.length) return;

        const existing = answers[currentIndex];
        const q = quiz.questions[currentIndex];
        const multiple = Array.isArray(q?.correct);

        if (existing !== undefined) {
            setCurrentAnswer(existing);
        } else {
            setCurrentAnswer(multiple ? [] : undefined);
        }
    }, [currentIndex, quiz, answers]);
    const [imageError, setImageError] = useState(false);

    const fetchQuiz = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/chapter-quizzes/${chapterId}`);
            setQuiz(res.data);
            setAnswers({});
            setResult(null);
            setCurrentIndex(0);
            setCurrentAnswer(undefined);
        } catch (e) {
            toast.error("Eroare la încărcare");
            setQuiz(null);
        } finally {
            setLoading(false);
        }
    };

    const currentChapter = chapters.find((ch) => ch.id === chapterId);
    const chapterTitle = currentChapter?.title || chapterId;

    const totalQuestions = quiz?.questions?.length || 0;
    const currentQuestion = quiz?.questions?.[currentIndex];
    const isMultiple = Array.isArray(currentQuestion?.correct);

    const stats = useMemo(() => {
        if (!quiz?.questions?.length) {
            return {
                resolved: 0,
                remaining: 0,
                correct: 0,
                wrong: 0,
            };
        }

        let resolved = 0;
        let correct = 0;
        let wrong = 0;

        quiz.questions.forEach((q, idx) => {
            const userAnswer = answers[idx];
            const userIndices = normalizeToIndices(userAnswer);

            if (userIndices.length === 0) return;

            resolved += 1;

            const correctIndices = normalizeToIndices(q.correct);
            const isCorrect = arraysEqual(userIndices, correctIndices);

            if (isCorrect) correct += 1;
            else wrong += 1;
        });

        return {
            resolved,
            remaining: quiz.questions.length - resolved,
            correct,
            wrong,
        };
    }, [quiz, answers]);

    const handleSelect = (optionIndex) => {
        if (!currentQuestion) return;

        if (isMultiple) {
            const current = Array.isArray(currentAnswer) ? currentAnswer : [];
            const updated = current.includes(optionIndex)
                ? current.filter((i) => i !== optionIndex)
                : [...current, optionIndex].sort((a, b) => a - b);

            setCurrentAnswer(updated);
        } else {
            setCurrentAnswer(optionIndex);
        }
    };

    const hasSelection = isMultiple
        ? Array.isArray(currentAnswer) && currentAnswer.length > 0
        : currentAnswer !== undefined;

    const commitCurrentAnswer = () => {
        if (!hasSelection) return false;

        setAnswers((prev) => ({
            ...prev,
            [currentIndex]: currentAnswer,
        }));

        return true;
    };

    const prev = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prevIndex) => prevIndex - 1);
        }
    };

    const next = () => {
        if (!commitCurrentAnswer()) {
            toast.error("Selectează un răspuns înainte să continui");
            return;
        }

        if (currentIndex < totalQuestions - 1) {
            setCurrentIndex((prevIndex) => prevIndex + 1);
        }
    };

    const submit = async () => {
        if (!quiz) return;

        if (!commitCurrentAnswer()) {
            toast.error("Selectează un răspuns înainte să trimiți");
            return;
        }

        const finalAnswers = {
            ...answers,
            [currentIndex]: currentAnswer,
        };

        if (Object.keys(finalAnswers).length < quiz.questions.length) {
            toast.error(
                `Răspunde la toate întrebările (${Object.keys(finalAnswers).length}/${quiz.questions.length})`
            );
            return;
        }

        try {
            setSubmitting(true);
            const res = await axios.post(`${API}/chapter-quizzes/${quiz.id}/submit`, {
                answers: finalAnswers,
            });
            setResult(res.data);
        } catch (e) {
            toast.error("Eroare la submit");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="p-10">Loading...</div>;
    }

    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
        return (
            <div className="min-h-screen bg-slate-100 p-4 flex justify-center">
                <div className="w-full max-w-5xl">
                    <div className="mb-4">
                        <button
                            onClick={() => navigate("/teste/capitole")}
                            className="flex items-center gap-2 text-slate-600"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Înapoi la capitole
                        </button>
                    </div>

                    <div className="bg-white p-8 rounded-xl shadow text-center text-slate-600">
                        Nu există încă întrebări pentru acest capitol.
                    </div>
                </div>
            </div>
        );
    }

    if (result) {
        const wrongAnswers = result.results?.filter(r => !r.is_correct) || [];
        const correctAnswers = result.results?.filter(r => r.is_correct) || [];
        
        return (
            <div className="min-h-screen bg-slate-100 p-4">
                <div className="max-w-4xl mx-auto">
                    {/* Header cu rezultat */}
                    <div className={`rounded-2xl shadow-xl p-8 mb-6 text-center ${
                        result.passed 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-r from-red-500 to-rose-600'
                    } text-white`}>
                        <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                            {result.passed ? (
                                <Trophy className="w-10 h-10" />
                            ) : (
                                <XCircle className="w-10 h-10" />
                            )}
                        </div>
                        <h1 className="text-4xl font-bold mb-2">
                            {result.passed ? "ADMIS" : "RESPINS"}
                        </h1>
                        <p className="text-5xl font-black mb-2">{result.score}%</p>
                        <p className="text-xl opacity-90">
                            {result.correct} corecte din {result.total} întrebări
                        </p>
                        <p className="text-sm opacity-75 mt-2">
                            Minim necesar: {result.passing_score || 80}%
                        </p>
                    </div>

                    {/* Statistici rapide */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-green-700">{correctAnswers.length}</p>
                            <p className="text-sm text-green-600">Răspunsuri corecte</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                            <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-700">{wrongAnswers.length}</p>
                            <p className="text-sm text-red-600">Răspunsuri greșite</p>
                        </div>
                    </div>

                    {/* Întrebări greșite - detaliate */}
                    {wrongAnswers.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-500" />
                                Întrebări greșite ({wrongAnswers.length})
                            </h2>
                            <div className="space-y-4">
                                {wrongAnswers.map((item, idx) => (
                                    <WrongAnswerCard key={idx} item={item} index={idx} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Butoane de acțiune */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            onClick={() => {
                                setResult(null);
                                setAnswers({});
                                setCurrentIndex(0);
                                setCurrentAnswer(undefined);
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Încearcă din nou
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate("/teste/capitole")}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Înapoi la capitole
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 p-4 flex justify-center">
            <div className="w-full max-w-6xl">
                <div className="mb-4">
                    <button
                        onClick={() => navigate("/teste/capitole")}
                        className="flex items-center gap-2 text-slate-600"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Înapoi la capitol
                    </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                        <p className="text-3xl font-bold text-slate-700">{stats.resolved}</p>
                        <p className="text-sm uppercase tracking-wide text-slate-500">Rezolvate</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                        <p className="text-3xl font-bold text-slate-700">{stats.remaining}</p>
                        <p className="text-sm uppercase tracking-wide text-slate-500">Rămase</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                        <p className="text-3xl font-bold text-green-600">{stats.correct}</p>
                        <p className="text-sm uppercase tracking-wide text-slate-500">Corecte</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                        <p className="text-3xl font-bold text-red-500">{stats.wrong}</p>
                        <p className="text-sm uppercase tracking-wide text-slate-500">Greșite</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                        <h1 className="text-2xl md:text-3xl font-bold mb-2 uppercase">
                            {chapterTitle}
                        </h1>
                        <p className="text-blue-100">
                            Întrebarea {currentIndex + 1} din {totalQuestions}
                        </p>
                    </div>

                    <div className="p-6 md:p-8">
                        <h2 className="text-2xl md:text-4xl text-slate-800 leading-snug mb-8">
                            {currentQuestion.question}
                        </h2>

                        <div
                            className={`grid gap-4 items-start ${currentQuestion.image_url ? "lg:grid-cols-[1.2fr_0.8fr]" : "grid-cols-1"
                                }`}
                        >
                            <div className="space-y-4 order-2 lg:order-1">
                                {currentQuestion.options
                                    .filter((opt) => opt !== null && opt !== undefined && String(opt).trim() !== "")
                                    .map((opt, i) => {
                                        const selected = isMultiple
                                            ? Array.isArray(currentAnswer) && currentAnswer.includes(i)
                                            : currentAnswer === i;

                                        return (
                                            <div
                                                key={i}
                                                onClick={() => handleSelect(i)}
                                                className={`flex items-stretch rounded-lg border cursor-pointer overflow-hidden transition-all ${selected
                                                    ? "border-green-500 shadow-md"
                                                    : "border-slate-300 hover:border-blue-400"
                                                    }`}
                                            >
                                                <div
                                                    className={`w-20 md:w-28 flex items-center justify-center text-3xl md:text-5xl font-semibold ${selected
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-slate-100 text-slate-600"
                                                        }`}
                                                >
                                                    {INDEX_TO_LETTER[i]}
                                                </div>

                                                <div className="flex-1 p-4 md:p-6 text-lg md:text-2xl text-slate-800 flex items-center">
                                                    {opt}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>

                            {currentQuestion.image_url && !imageError && (
                                <div className="order-1 lg:order-2 flex justify-center">
                                    <img
                                        key={`${currentIndex}-${currentQuestion.image_url}`}
                                        src={currentQuestion.image_url}
                                        alt="Imagine întrebare"
                                        className="w-full max-w-[500px] rounded-lg object-contain border bg-white p-2"
                                        onError={() => setImageError(true)}
                                    />
                                </div>
                            )}
                        </div>

                        {isMultiple && (
                            <p className="mt-6 text-amber-600 font-medium text-lg">
                                Selectează toate răspunsurile corecte
                            </p>
                        )}

                        <div className="flex flex-wrap justify-center gap-4 mt-10">
                            <Button
                                variant="outline"
                                onClick={prev}
                                disabled={currentIndex === 0}
                                className="min-w-[180px] h-14 text-lg"
                            >
                                Înapoi
                            </Button>

                            {currentIndex === totalQuestions - 1 ? (
                                <Button
                                    onClick={submit}
                                    disabled={!hasSelection || submitting}
                                    className="min-w-[220px] h-14 text-lg bg-green-600 hover:bg-green-700"
                                >
                                    {submitting ? "Se trimite..." : "Răspunde"}
                                </Button>
                            ) : (
                                <Button
                                    onClick={next}
                                    disabled={!hasSelection}
                                    className="min-w-[220px] h-14 text-lg bg-blue-600 hover:bg-blue-700"
                                >
                                    Următoarea
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}